import { useTranslate, HttpError } from '@refinedev/core';
import { Create, useForm } from '@refinedev/antd';
import { Form, Input, Radio, Tabs, Divider } from 'antd';
import type {
  ManagedCollectionDetailOut,
  ManagedCollectionIn,
} from '@/api/generated/schemas';
import { ModStatus, ManagedCollectionKind } from '@/api/generated/schemas';
import { CollectionImageFields } from '@/components/common/CollectionImageFields';
import {
  CollectionDataFields,
  normalizeCollectionData,
} from '@/components/query-builder';
import { LabelsField } from '@/components/common/LabelsField';
import { SearchPopularityField } from '@/components/common/SearchPopularityField';

export const ManagedCollectionCreate = () => {
  const t = useTranslate();
  const { formProps, saveButtonProps } = useForm<
    ManagedCollectionDetailOut,
    HttpError,
    ManagedCollectionIn
  >({
    resource: 'user_catalog.managed_collections',
  });

  const handleFinish = (values: ManagedCollectionIn) =>
    formProps.onFinish?.(normalizeCollectionData(values));

  return (
    <Create saveButtonProps={saveButtonProps}>
      <Form {...formProps} onFinish={handleFinish} layout="vertical">
        <Tabs
          items={[
            {
              key: 'basic',
              label: t('base.sections.basic_info'),
              children: (
                <>
                  <Form.Item
                    name="name"
                    label={t('user_catalog.managed_collections.fields.name')}
                    rules={[{ required: true }]}
                  >
                    <Input />
                  </Form.Item>

                  <Form.Item
                    name="kind"
                    label={t('user_catalog.managed_collections.fields.kind')}
                    rules={[{ required: true }]}
                  >
                    <Radio.Group
                      optionType="button"
                      buttonStyle="solid"
                      options={Object.values(ManagedCollectionKind).map(
                        (value) => ({
                          label: t(
                            `user_catalog.managed_collections.kind.${value}`
                          ),
                          value,
                          disabled: value === ManagedCollectionKind.personal,
                        })
                      )}
                    />
                  </Form.Item>

                  <Form.Item
                    name="description"
                    label={t(
                      'user_catalog.managed_collections.fields.description'
                    )}
                  >
                    <Input.TextArea rows={4} />
                  </Form.Item>

                  <LabelsField />

                  <Divider>{t('base.sections.system_info')}</Divider>

                  <Form.Item
                    name="mod_status"
                    label={t('base.fields.mod_status')}
                    rules={[{ required: true }]}
                    initialValue={ModStatus.enabled}
                  >
                    <Radio.Group
                      optionType="button"
                      buttonStyle="solid"
                      options={Object.values(ModStatus).map((value) => ({
                        label: t(`base.mod_status.${value}`),
                        value,
                      }))}
                    />
                  </Form.Item>
                </>
              ),
            },
            {
              key: 'collection',
              label: t('base.sections.collection_info'),
              children: (
                <>
                  <Form.Item
                    name="header"
                    label={t('user_catalog.managed_collections.fields.header')}
                  >
                    <Input />
                  </Form.Item>

                  <Form.Item
                    name="content"
                    label={t('user_catalog.managed_collections.fields.content')}
                  >
                    <Input.TextArea rows={4} />
                  </Form.Item>

                  <CollectionImageFields />

                  <CollectionDataFields mode="create" />
                </>
              ),
            },
            {
              key: 'search',
              label: t('user_catalog.common.tabs.search'),
              children: <SearchPopularityField />,
            },
          ]}
        />
      </Form>
    </Create>
  );
};
